import { Link } from 'react-router-dom';
import type { flowIdeas } from '@repletoryreach/sdk-api';
import { Badge, Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Checkbox } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { formatCredits } from '../../../lib/credits';
import { flagText } from './lib/data';

const MUTED = 'var(--rr-color-text-muted)';
const PRIORITY_COLOR = { high: 'red', medium: 'orange', low: 'gray' } as const;

/**
 * One suggested flow: what it is for and what comes of it, how it starts, its steps (before and after, for a change to
 * an existing flow), why the AI suggests it and on what, what it needs from outside, anything the code found to check,
 * and what building it costs. The tick box picks it; nothing unpicked is ever built.
 */
export function SuggestionCard({ item, picked, onPick, onAsk, fallbackMinor }: {
  item: flowIdeas.FlowIdeaItem;
  picked: boolean;
  onPick: (on: boolean) => void;
  onAsk: () => void;
  /** The building model's price a flow, for a suggestion without its own estimate. */
  fallbackMinor?: number | null;
}) {
  const t = useT();
  const p = item.proposal;
  const minor = item.build_minor ?? fallbackMinor ?? null;
  const improve = p.kind === 'improve' && p.improves;

  return (
    <article className={`rr-ideas__card${picked ? ' is-picked' : ''}`} data-testid="ideas-card" data-n={item.n}>
      <div className="rr-ideas__card-head">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="wrap">
            <Text role="caption" c={MUTED}>{t('flowIdeas.card.number', { n: item.n })}</Text>
            <Badge size="sm" variant="light" color={PRIORITY_COLOR[p.priority]}>{t(`flowIdeas.priority.${p.priority}`)}</Badge>
            {improve ? <Badge size="sm" variant="light" color="blue">{t('flowIdeas.card.improve')}</Badge>
              : <Badge size="sm" variant="light" color="green">{t('flowIdeas.card.new')}</Badge>}
            {p.goal && <Badge size="sm" variant="outline">{t(`flowIdeas.goal.${p.goal}`)}</Badge>}
          </Group>
          <Text role="bodySmall" fw={700} style={{ fontSize: 16 }}>{p.name}</Text>
          {improve && (
            <Text role="caption" c={MUTED} data-testid="ideas-improves">
              {t('flowIdeas.card.improves')} <Link to={`/flows/${p.improves!.flow_id}/builder`}>{p.improves!.flow_name}</Link>
            </Text>
          )}
        </Stack>
        <Checkbox aria-label={t('flowIdeas.card.pick', { name: p.name })} checked={picked} onChange={onPick} data-testid="ideas-pick" />
      </div>

      <Stack gap={6}>
        <Text role="bodySmall">{p.description}</Text>
        <Field label={t('flowIdeas.card.purpose')}>{p.purpose}</Field>
        <Field label={t('flowIdeas.card.outcome')}>{p.outcome}</Field>
        <Field label={t('flowIdeas.card.starts')}><Starts starts={p.starts} /></Field>
      </Stack>

      {improve && p.steps_before.length > 0 ? (
        <div className="rr-ideas__compare">
          <Steps label={t('flowIdeas.card.stepsNow')} steps={p.steps_before} />
          <Steps label={t('flowIdeas.card.stepsAfter')} steps={p.steps} />
        </div>
      ) : <Steps label={t('flowIdeas.card.steps')} steps={p.steps} />}

      <Stack gap={4}>
        <span className="rr-ideas__label">{t('flowIdeas.card.why')}</span>
        <Text role="bodySmall">{p.why}</Text>
        {p.sources.length > 0 && (
          <div className="rr-ideas__sources" aria-label={t('flowIdeas.card.sources')}>
            {p.sources.map((s) => <span key={s.label} className="rr-ideas__source" title={t(`flowIdeas.source.${s.kind}`)}>{s.label} · {s.name}</span>)}
          </div>
        )}
      </Stack>

      {(p.uses.flows.length > 0 || p.uses.bookings || p.uses.tickets) && (
        <Field label={t('flowIdeas.card.uses')}>
          {[
            ...p.uses.flows.map((f) => t('flowIdeas.card.usesFlow', { name: f.name })),
            ...(p.uses.bookings ? [t('flowIdeas.card.usesBookings')] : []),
            ...(p.uses.tickets ? [t('flowIdeas.card.usesTickets')] : [])
          ].join(' · ')}
        </Field>
      )}

      {p.needs.length > 0 && (
        <Stack gap={2}>
          <span className="rr-ideas__label">{t('flowIdeas.card.needs')}</span>
          {p.needs.map((n, i) => (
            <Text key={i} role="caption">{t(`flowIdeas.need.${n.kind}`)}: {n.what}</Text>
          ))}
        </Stack>
      )}

      {item.flags.length > 0 && (
        <ul className="rr-ideas__flags" data-testid="ideas-flags" aria-label={t('flowIdeas.card.check')}>
          {item.flags.map((f, i) => <li key={i}>{flagText(t, f)}</li>)}
        </ul>
      )}

      <div className="rr-ideas__card-foot">
        <Text role="caption" c={MUTED} data-testid="ideas-card-price">
          {minor == null ? t('flowIdeas.card.priceUnknown') : t('flowIdeas.card.price', { credits: formatCredits(minor, t) })}
        </Text>
        <Button size="xs" variant="subtle" onClick={onAsk} data-testid="ideas-ask">{t('flowIdeas.card.ask')}</Button>
      </div>
    </article>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="rr-ideas__label">{label}</span>
      <Text role="bodySmall">{children}</Text>
    </div>
  );
}

function Steps({ label, steps }: { label: string; steps: string[] }) {
  return (
    <div>
      <span className="rr-ideas__label">{label}</span>
      <ol className="rr-ideas__steps">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
    </div>
  );
}

function Starts({ starts }: { starts: flowIdeas.FlowIdeaProposal['starts'] }) {
  const t = useT();
  if (starts.type === 'entry' && starts.entry) return <>{t('flowIdeas.starts.entry', { entry: t(`flows.entry.${starts.entry}.name`) })}</>;
  if (starts.type === 'intent' && starts.intent) return <>{t('flowIdeas.starts.intent', { intent: starts.intent })}</>;
  if (starts.keywords.length > 0) return <>{t('flowIdeas.starts.keywords', { keywords: starts.keywords.map((k) => `“${k}”`).join(', ') })}</>;
  return <>{t('flowIdeas.starts.unknown')}</>;
}
